import React, {useRef, useState} from "react";
import {useData} from "../../../hooks/useData";
import {GetStrikesResponse, PlayerLite, PostStrikeResponse, Strike, StrikeType} from "../interfaces";
import OverlayBase from "../overlay/OverlayBase";
import SvgLoader from "../../../svg/SvgLoader";
import {doFetch} from "../../../util/fetch";
import {relativeDate} from "../../../util/timeConverter";
import {useServerId} from "../useServerId";

export default function PlayerStrikesOverlay({hide, player}: {hide: () => void, player: PlayerLite}) {
    const serverId = useServerId()
    const [newStrikeId, setNewStrikeId] = useState<string>("")

    const data = useData<GetStrikesResponse>(`/api/client/players/strikes?serverId=${serverId}&steamId=${player.steamId}&n=${newStrikeId}`)

    return (
        <OverlayBase hide={hide}>
            {data.isLoading && (
                <div className="flex flex-col items-center">
                    <SvgLoader/>
                    <p className="mt-4 text-white text-lg font-semibold tracking-wide">Loading...</p>
                </div>
            )}
            {data.data && <StrikesOverlay strikes={data.data.strikes} player={player} />}
            {data.error && <p className="text-red-500">Failed to load data: {data.error}</p>}
        </OverlayBase>
    )

    function StrikesOverlay({strikes, player}: {strikes: Strike[], player: PlayerLite}){
        const newStrikeRef = useRef<HTMLTextAreaElement>(null)

        const saveStrike = () => {
            if (!newStrikeRef.current) {
                return;
            }

            doFetch<PostStrikeResponse>(`/api/client/players/strikes?serverId=${serverId}`, {
                method: "POST",
                body: JSON.stringify({
                    steamId: player.steamId,
                    text: newStrikeRef.current.value
                }),
                headers: {
                    "Content-Type": "application/json"
                }
            }).then(r => {
                if (r.ok && r.data) {
                    if (newStrikeRef.current){
                        newStrikeRef.current.value = ""
                    }
                    setNewStrikeId(r.data.id)
                }
            })
        }

        const deleteStrike = (id: string) => {

            doFetch<PostStrikeResponse>(`/api/client/players/strikes?serverId=${serverId}&strikeId=${id}`, {
                method: "DELETE"
            }).then(r => {
                if (r.ok) {
                    setNewStrikeId('-' + id)
                }
            })
        }

        return (<div>
            <div className="flex flex-row align-top">
                <div className="flex flex-col select-text">
                    <h3 className="text-xl font-bold">{strikes.length} Strikes for {player.displayName}</h3>
                </div>
            </div>
            <div className="flow-root mt-2">
                <textarea className="rounded-md border-neutral-800 border p-2 w-full bg-neutral-600"
                          ref={newStrikeRef} placeholder="Click to add a strike"></textarea>
                <button className="bg-primary hover:bg-primary-hover rounded-md px-2 py-1 float-right" onClick={saveStrike}>Save</button>
            </div>
            <div className="space-y-2 mt-3">
                {strikes.map(s =>
                    <div key={s.id}>
                        {s.type === StrikeType.GENERIC && <button className="bg-red-500 hover:bg-red-600 rounded-md px-1 float-right"
                                 onClick={() => deleteStrike(s.id)}>Delete</button>}
                        <div className="bg-neutral-600 rounded-md p-2">
                            {(s.text ?? "").split('\n').map((x, i) => <p key={i}>{x}</p>)}
                        </div>
                        <span className="text-neutral-200 ms-2 text-sm" >{getStrikeKind(s.type)} by {s.author ?? <i>Unknown User</i>} {relativeDate(s.created, undefined, undefined, true)}</span>
                    </div>
                )}
            </div>
        </div>)

        function getStrikeKind(type: StrikeType){
            if (type === StrikeType.BAN){
                return <b className="text-red-500">Banned</b>
            }
            else if (type === StrikeType.KICK){
                return <b className="text-orange-500">Kicked</b>
            }

            return <b className="text-gray-400">Manual Strike</b>
        }
    }
}